import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom'; // 1. Import useNavigate hook
import { FaPlus, FaMinus } from 'react-icons/fa';
import './Finance.css';
import financeHero from '../../../assets/Services/Finance.png';
import CustomButton from '../../Button/CustomButton';

const FinanceDetail = () => {
  const [activeFAQ, setActiveFAQ] = useState(null);
  const navigate = useNavigate(); // 2. Initialize the navigate function

  const faqData = [
    {
      q: "1. When should we involve Dazbog in a project?",
      a: "You can involve us at any stage—early planning, project finance, refinancing, or during transactions. Early involvement helps create stronger, lender-ready structures."
    },
    {
      q: "2. Do you support projects only as an advisor, or also during execution?",
      a: " We provide financial and transaction advisory and also support during execution by working closely with developers, lenders, and EPC partners."
    },
    {
      q: "3. What types of financial services do you provide?",
      a: "We support project finance, M&A, equity raising, refinancing, working capital structuring, and financial due diligence for renewable energy projects."
    },
    {
      q: "4. Who do you typically work with?",
      a: "We work with developers, investors, banks, financial institutions, and EPC partners across solar, wind, BESS, and hybrid projects."
    }
  ];

  // Animation variants for sections
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  return (
    <div className="service-detail-page">
      {/* --- HERO SECTION --- */}
      <section className="detail-hero">
        <div className="container">
          <div className="detail-hero-flex">
            <motion.div
              className="detail-hero-content"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>Financial & Transaction Advisory for Renewable Energy Projects</h1>
              <p>We work with developers, investors, and institutions to turn renewable energy ideas into funded and ready-to-build projects.
              From early planning to financial close, we stay involved and help move things forward with clarity and confidence.</p>

              {/* 3. Updated onClick to use navigate function */}
              <CustomButton
                variant="white"
                size="lg"
                text=" Discuss Your Project"
                onClick={() => navigate("/contact")}
              />
            </motion.div>
            <motion.div
              className="detail-hero-image"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
            >
              <img src={financeHero} alt="Finance Advisory" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- HOW WE SUPPORT SECTION --- */}
      <section className="support-info-section">
        <div className="container">
          <motion.div
            className="support-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <h2>How We Support Your Project</h2>
            <p >Renewable energy projects involve many moving parts — technical planning, financial structuring, lender discussions, and risk reviews.</p>
            <p >Our role is to simplify this process.</p>
            <p className="support-lead">We work alongside your team to make the project clear, structured, and ready for funding. We focus on reducing risks, organizing financial details properly, and making sure everything is in place before investors step in.</p>
          </motion.div>

          <div className="services-list-container">
            <motion.h3
              className="list-main-title"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              Our Financial & Transaction Services
            </motion.h3>

            <motion.div
              className="services-breakdown-grid"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              {[
                {
                  title: "1. Project Finance Support",
                  text1: "Every project needs the right financial structure.",
                  text2: "We help shape the financial plan in a way that works for banks, investors, and developers.",
                  listTitle: "Our support includes:",
                  list: ["Preparing clear financial models", "Planning debt and equity structure", "Understanding lender requirements", "Making the project funding-ready"],
                  outro: "We focus on building a solid financial foundation from the start."
                },
                {
                  title: "2. Due Diligence & Risk Review",
                  text1: "Before funding happens, every detail matters.",
                  text2: "We carefully review technical, financial, and commercial aspects of the project to identify possible risks early.",
                  listTitle: "This includes:",
                  list: ["Coordinating technical reviews", "Checking cost assumptions", "Reviewing contracts and agreements", "Highlighting potential risk areas"],
                  outro: "Fixing issues early saves time and avoids problems later."
                },
                {
                  title: "3. Investor & Lender Engagement",
                  text1: "Raising funds requires clear communication.",
                  text2: "We support discussions with banks and investors throughout the transaction process.",
                  listTitle: "Our role includes:",
                  list: ["Preparing investor presentations", "Coordinating with lenders", "Managing due diligence queries", "Supporting negotiations"],
                  outro: "We help ensure conversations stay structured and productive."
                },
                {
                  title: "4. Transaction Closure & Ongoing Support",
                  text1: "Our involvement doesn’t stop at funding approval.",
                  text2: "We stay engaged until financial closure and continue supporting key milestones during execution.",
                  listTitle: "Our support includes:",
                  list: ["Coordinating financial close", "Assisting during post-close stages", "Supporting refinancing when needed"],
                  outro: "We believe successful closure is just as important as initial planning."
                }
              ].map((service, index) => (
                <motion.div key={index} className="service-text-block" variants={fadeInUp}>
                  <h4>{service.title}</h4>
                  <p>{service.text1}</p>
                  <p>{service.text2}</p>
                  <p>{service.listTitle}</p>
                  <ul>
                    {service.list.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p>{service.outro}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <motion.div
            className="projects-tags-box"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3>Projects We Work On</h3>
            <div className="tag-flex">
              {[
                { name: "Solar IPP", path: "/projects/solar-ipp" },
                { name: "Solar EPC Project", path: "/projects/solar-epc" },
                { name: "Solar Hybrid EPC Project", path: "/projects/solar-hybrid" },
                { name: "Solar + BESS EPC Project", path: "/projects/solar-bess" }
              ].map((tag, i) => (
                <span
                  key={i}
                  className="project-tag-link"
                  onClick={() => navigate(tag.path)}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section className="faq-detail-section">
        <div className="container">
          <div className="faq-flex-row">
            <motion.div
              className="faq-left-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2>Financial & Transaction Advisory – FAQs</h2>
              <p>Clear answers to common questions about our financial and transaction advisory support.</p>
            </motion.div>
            <div className="faq-right-accordion">
              {faqData.map((faq, i) => (
                <motion.div
                  key={i}
                  className="faq-detail-item"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <div className="faq-detail-q" onClick={() => setActiveFAQ(activeFAQ === i ? null : i)}>
                    <span>{faq.q}</span>
                    {activeFAQ === i ? <FaMinus className="faq-icon" /> : <FaPlus className="faq-icon" />}
                  </div>
                  <AnimatePresence>
                    {activeFAQ === i && (
                      <motion.div
                        className="faq-detail-a"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        <p>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinanceDetail;